(() => {
  if (customElements.get('lc-cart-care')) return;
  const root = () => window.Shopify?.routes?.root || '/';
  let queue = Promise.resolve();
  let recommendationsDismissed = false;
  const disclosures = new Map();
  const preferenceDrafts = new Map();
  const preferenceKey = field => field.hasAttribute('data-lc-note') ? 'note' : 'attribute:' + field.dataset.lcAttribute;
  const rememberField = field => {
    field.dataset.lcDirty = 'true';
    preferenceDrafts.set(preferenceKey(field), {value:field.value, dirty:true});
  };
  const serial = task => {
    const next = queue.catch(() => {}).then(task);
    queue = next;
    return next;
  };

  async function request(path, payload) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);

    try {
      const response = await fetch(root() + path, {
        method: payload ? 'POST' : 'GET',
        credentials: 'same-origin',
        headers: payload
          ? {'Content-Type': 'application/json', Accept: 'application/json'}
          : {Accept: 'application/json'},
        body: payload ? JSON.stringify(payload) : undefined,
        cache: 'no-store',
        signal: controller.signal
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Cart request failed');
      }

      return data;
    } finally {
      clearTimeout(timeout);
    }
  }

  const isCartPage = () => location.pathname.replace(/\/$/, '').endsWith('/cart');

  function packagingPlan(cart, config, selection) {
    const feeIds = config.packagingVariantIds || [config.bagId];
    const fees = cart.items.filter(item => feeIds.includes(Number(item.variant_id || item.id)));
    const kinds = new Map((config.packagingItems || []).map(item => [Number(item.variantId), item.kind]));
    const eligible = cart.items.filter(item => ['bag', 'wrap', 'both'].includes(kinds.get(Number(item.variant_id || item.id))));
    const selected = selection === undefined ? fees.length > 0 : selection;
    const requested = selected && eligible.length > 0;
    const canonical = fees.find(item => Number(item.variant_id || item.id) === config.bagId);
    const updates = {};
    for (const fee of fees) {
      const quantity = requested && fee === canonical ? 1 : 0;
      if (fee.quantity !== quantity) updates[fee.key] = quantity;
    }
    const attributes = {};
    const nextAttributes = {
      'LC gift packaging': requested ? 'Requested — one fee per order' : '',
      'LC gift packaging scope': ''
    };
    if (requested) {
      const units = {bag: 0, wrap: 0, both: 0};
      for (const item of eligible) {
        const kind = kinds.get(Number(item.variant_id || item.id));
        units[kind] += item.quantity;
        const label = {bag: 'Gift bag', wrap: 'Gift wrap', both: 'Gift bag or wrap'}[kind];
        const title = String(item.product_title || item.title || '').slice(0, 115);
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? ' · ' + String(item.variant_title).slice(0, 45) : '';
        nextAttributes['LC gift item ' + item.key] = label + ' | ' + title + variant + ' | Qty ' + item.quantity + ' | Variant ' + Number(item.variant_id || item.id);
      }
      nextAttributes['LC gift packaging scope'] = 'Per order | Bag: ' + units.bag + ' | Wrap: ' + units.wrap + ' | Bag or wrap: ' + units.both + '. Details in LC gift item fields.';
    }
    for (const key of Object.keys(cart.attributes || {})) {
      if (key.startsWith('LC gift item ') && !Object.hasOwn(nextAttributes, key)) nextAttributes[key] = '';
    }
    for (const [key, value] of Object.entries(nextAttributes)) {
      if (String(cart.attributes?.[key] || '') !== value) attributes[key] = value;
    }
    return {updates, attributes, add: requested && !canonical, unavailable: selection === true && !eligible.length};
  }

  function merchandiseTotal(cart, config) {
    return Math.max(
      0,
      cart.total_price - cart.items
        .filter(item =>
          (config.packagingVariantIds || [config.bagId]).includes(Number(item.variant_id || item.id)) ||
          item.id === config.signatureId ||
          item.gift_card
        )
        .reduce((sum, item) => sum + item.final_line_price, 0)
    );
  }

  function snapshotFields() {
    const attributes = {};

    preferenceDrafts.forEach((draft, key) => {
      if (draft.dirty && key.startsWith('attribute:')) {
        attributes[key.slice(10)] = draft.value;
      }
    });

    document.querySelectorAll('lc-cart-care [data-lc-attribute]').forEach(field => {
      if (field.dataset.lcDirty === 'true') {
        attributes[field.dataset.lcAttribute] = field.value;
      }
    });

    return attributes;
  }

  const snapshotNote = () => preferenceDrafts.get('note')?.dirty
    ? preferenceDrafts.get('note').value
    : undefined;

  function syncSampleChoices() {
    document.querySelectorAll('[data-lc-samples]').forEach(group => {
      const field = group.querySelector('[data-lc-sample-field]');
      if (!field) return;
      let message = group.dataset.defaultMessage;
      let summary = group.dataset.defaultSummary;
      if (field.matches('select') && field.value) {
        const selected = field.selectedOptions[0];
        if (selected) {
          message = selected.dataset.sampleMessage;
          summary = selected.textContent.trim();
        }
      }
      group.querySelectorAll('[data-lc-sample-choice]').forEach(button => {
        const selected = button.dataset.lcSampleChoice === field.value;
        button.setAttribute('aria-pressed', String(selected));
        if (selected) {
          message = button.dataset.sampleMessage;
          summary = (button.querySelector('[data-lc-sample-label]') || button).textContent.trim();
        }
      });
      const summaryNode = group.querySelector('[data-lc-sample-summary]');
      if (summaryNode) summaryNode.textContent = summary;
      group.querySelector('[data-lc-sample-message]').textContent = message;
      group.querySelector('[data-lc-sample-clear]').hidden = !field.value;
    });
  }

  function markSaved(attributes) {
    Object.entries(attributes).forEach(([key, value]) => {
      const draft = preferenceDrafts.get('attribute:' + key);
      if (draft?.value === value) draft.dirty = false;
    });

    document.querySelectorAll('lc-cart-care [data-lc-attribute]').forEach(field => {
      if (
        Object.hasOwn(attributes, field.dataset.lcAttribute) &&
        field.dataset.lcDirty !== 'true'
      ) {
        field.value = attributes[field.dataset.lcAttribute];
      }

      if (attributes[field.dataset.lcAttribute] === field.value) {
        delete field.dataset.lcDirty;
      }
    });
    syncSampleChoices();
  }

  function markNoteSaved(note) {
    const draft = preferenceDrafts.get('note');
    if (draft?.value === note) draft.dirty = false;

    document.querySelectorAll('lc-cart-care [data-lc-note]').forEach(field => {
      if (field.dataset.lcDirty !== 'true') field.value = note;
      if (field.value === note) delete field.dataset.lcDirty;
    });
  }

  class LCCartCare extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;

      const source = this.querySelector('[data-lc-config]');
      if (!source) return;

      try {
        this.config = JSON.parse(source.textContent);
      } catch {
        return;
      }

      this.ready = true;
      this.abort = new AbortController();

      this.querySelectorAll('[data-lc-attribute], [data-lc-note]').forEach(field => {
        const draft = preferenceDrafts.get(preferenceKey(field));
        if (!draft) return;

        field.value = draft.value;
        if (draft.dirty) field.dataset.lcDirty = 'true';
      });
      syncSampleChoices();

      this.querySelectorAll('[data-lc-disclosure]').forEach(details => {
        const key = this.config.sectionId + ':' + details.dataset.lcDisclosure;

        if (disclosures.has(key)) {
          details.open = disclosures.get(key);
        }

        details.addEventListener(
          'toggle',
          () => disclosures.set(key, details.open),
          {signal: this.abort.signal}
        );
      });

      this.querySelectorAll('button, input[data-lc-service], select[data-lc-sample-field]').forEach(control => {
        control.disabled = false;
      });

      this.addEventListener('input', event => {
        if (event.target.matches('[data-lc-attribute], [data-lc-note]')) {
          rememberField(event.target);
        }
      }, {signal: this.abort.signal});

      this.addEventListener('change', event => {
        const input = event.target;

        if (input.matches('[data-lc-service]')) {
          const selected = input.checked;

          this.run(() => this.setService(input.dataset.lcService, selected))
            .catch(() => {
              if (input.isConnected) input.checked = !selected;
            });
        } else if (input.matches('[data-lc-attribute], [data-lc-note]')) {
          rememberField(input);
          const sampleField = input.matches('[data-lc-sample-field]');
          if (sampleField) {
            document.querySelectorAll('[data-lc-sample-field]').forEach(field => {
              field.value = input.value;
            });
            syncSampleChoices();
          }
          this.run(async () => {
            await this.savePreferences();
            if (sampleField) this.status('');
          }).catch(() => {});
        }
      }, {signal: this.abort.signal});

      this.addEventListener('click', event => {
        const control = event.target.closest('button');
        if (!control || !this.contains(control)) return;

        if (control.matches('[data-lc-sample-choice], [data-lc-sample-clear]')) {
          if (this.busy) return;
          const field = this.querySelector('[data-lc-sample-field]');
          if (!field) return;
          const next = control.dataset.lcSampleChoice || '';
          const value = next === field.value ? '' : next;
          document.querySelectorAll('[data-lc-sample-field]').forEach(input => {
            input.value = value;
          });
          rememberField(field);
          syncSampleChoices();
          this.run(async () => {
            await this.savePreferences();
            this.status('');
          }).catch(() => {});
          return;
        }

        if (control.hasAttribute('data-lc-dismiss')) {
          recommendationsDismissed = true;

          try {
            sessionStorage.setItem('lc-cart-suggestions-dismissed', '1');
          } catch {}

          this.hidden = true;

          document.querySelectorAll('lc-cart-care[data-mode="recommendation"]')
            .forEach(element => {
              element.hidden = true;
            });

          this.closest('cart-drawer, .cart-page')
            ?.querySelector('lc-cart-care[data-mode="options"] summary')
            ?.focus();
        }

        if (control.hasAttribute('data-lc-add')) {
          this.run(() => this.addRecommendation()).catch(() => {});
        }

        if (control.hasAttribute('data-lc-save')) {
          this.run(() => this.savePreferences(true)).catch(() => {});
        }

        if (control.hasAttribute('data-lc-remove-signature')) {
          this.run(() => this.setService('signature', false)).catch(() => {});
        }
      }, {signal: this.abort.signal});

      if (this.config.mode === 'recommendation') {
        if (recommendationsDismissed) this.hidden = true;

        try {
          if (sessionStorage.getItem('lc-cart-suggestions-dismissed') === '1') {
            this.hidden = true;
          }

          const accepted = Number(
            sessionStorage.getItem('lc-cart-accepted-product')
          );

          if (accepted && this.config.productIds.includes(accepted)) {
            this.hidden = true;
          } else if (accepted) {
            sessionStorage.removeItem('lc-cart-accepted-product');
          }
        } catch {}
      }

      const needsSignatureCleanup =
        this.config.signatureIncluded && this.config.hasPaidSignature;

      if (this.config.mode === 'options' && (
        needsSignatureCleanup || this.config.hasPackaging || this.config.savedPackagingScope
      )) {
        this.run(async () => {
          const before = await request('cart.js');
          let cart = await this.normalizePackaging(before);
          cart = await this.normalizeSignature(cart);
          if (cart !== before) await this.publish(cart, cart.sections);
          else this.status('');
        }).catch(() => {});
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.ready = false;
    }

    status(message) {
      const node = this.querySelector('[data-lc-status]');

      if (node) {
        node.textContent = message;
        node.hidden = !message;
      }
    }

    async run(task) {
      if (this.busy) return;
      this.busy = true;

      this.querySelectorAll('button, input[data-lc-service], select[data-lc-sample-field]').forEach(control => {
        control.disabled = true;
      });

      this.status(this.config.strings.saving);

      try {
        await serial(task);
      } catch (error) {
        this.status(this.config.strings.error);
        throw error;
      } finally {
        this.busy = false;

        this.querySelectorAll('button, input[data-lc-service], select[data-lc-sample-field]').forEach(control => {
          control.disabled = false;
        });
      }
    }

    async savePreferences(force = false) {
      if (force) {
        this.querySelectorAll('[data-lc-attribute], [data-lc-note]')
          .forEach(rememberField);
      }

      let attributes = snapshotFields();
      let note = snapshotNote();
      let changed = false;

      while (Object.keys(attributes).length || note !== undefined) {
        const payload = {};

        if (Object.keys(attributes).length) payload.attributes = attributes;
        if (note !== undefined) payload.note = note;

        await request('cart/update.js', payload);
        markSaved(attributes);

        if (note !== undefined) markNoteSaved(note);

        changed = true;
        attributes = snapshotFields();
        note = snapshotNote();
      }

      this.status(this.config.strings.saved);
      return changed;
    }

    sections() {
      const sections = [];

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: {sections}
        })
      );

      return [...new Set([this.config.sectionId, ...sections])].slice(0, 5);
    }

    async publish(cart, sections) {
      if (await this.savePreferences()) sections = undefined;

      if (isCartPage()) {
        location.reload();
        return;
      }

      cart.sections = sections || {};

      const drawer = document.querySelector('cart-drawer');
      const drawerSection =
        drawer?.querySelector('lc-cart-care[data-section-id]')?.dataset.sectionId ||
        drawer?.closest('[id^="shopify-section-"]')
          ?.id.replace('shopify-section-', '');

      const validDrawer = html =>
        typeof html === 'string' &&
        new DOMParser()
          .parseFromString(html, 'text/html')
          .getElementById('shopify-section-' + drawerSection)
          ?.querySelector('cart-drawer');

      if (drawerSection && !validDrawer(cart.sections[drawerSection])) {
        const replacement = await request('?sections=' + encodeURIComponent(drawerSection));

        if (!validDrawer(replacement[drawerSection])) {
          location.assign(root() + 'cart');
          return;
        }

        cart.sections[drawerSection] = replacement[drawerSection];
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {baseEvent: 'lc-cart:change', cart}
      }));

      this.status('');
    }

    async normalizePackaging(cart, selected) {
      let plan = packagingPlan(cart, this.config, selected);
      if (plan.unavailable) throw new Error(this.config.strings.unavailable);
      if (plan.add) {
        if (!this.config.packagingAvailable) throw new Error(this.config.strings.unavailable);
        await request('cart/add.js', {items: [{id: this.config.bagId, quantity: 1, properties: {_lc_gift_packaging: 'per-order'}}]});
        cart = await request('cart.js');
        plan = packagingPlan(cart, this.config, selected);
      }
      if (Object.keys(plan.updates).length || Object.keys(plan.attributes).length) {
        cart = await request('cart/update.js', {
          updates: plan.updates,
          attributes: plan.attributes,
          sections: this.sections(),
          sections_url: location.pathname
        });
      }
      return cart;
    }

    async normalizeSignature(cart) {
      if (
        this.config.country !== 'CA' ||
        cart.currency !== 'CAD' ||
        merchandiseTotal(cart, this.config) < 35000
      ) {
        return cart;
      }

      const lines = cart.items.filter(
        item => item.id === this.config.signatureId
      );

      for (const line of lines) {
        cart = await request('cart/change.js', {
          id: line.key,
          quantity: 0,
          sections: this.sections(),
          sections_url: location.pathname
        });
      }

      return cart;
    }

    async addRecommendation() {
      await this.savePreferences();
      let cart = await request('cart.js');

      if (cart.items.some(
        item => item.product_id === this.config.selectedProductId
      )) {
        this.status(this.config.strings.cartChanged);
        await this.publish(cart);
        return;
      }

      const result = await request('cart/add.js', {
        items: [{id: this.config.selectedVariantId, quantity: 1}],
        sections: this.sections(),
        sections_url: location.pathname
      });

      cart = await request('cart.js');
      const normalized = await this.normalizeSignature(cart);

      try {
        sessionStorage.setItem(
          'lc-cart-accepted-product',
          String(this.config.selectedProductId)
        );
      } catch {}

      await this.publish(normalized, normalized.sections || result.sections);
    }

    async setService(kind, selected) {
      await this.savePreferences();
      let cart = await request('cart.js');

      if (kind === 'bag') {
        cart = await this.normalizePackaging(cart, selected);
        cart = await this.normalizeSignature(cart);
        await this.publish(cart, cart.sections);
        return;
      }

      const id = kind === 'bag'
        ? this.config.bagId
        : this.config.signatureId;

      const existing = cart.items.filter(item => item.id === id);
      let sections;

      if (
        kind === 'signature' &&
        this.config.country === 'CA' &&
        cart.currency === 'CAD' &&
        merchandiseTotal(cart, this.config) >= 35000
      ) {
        selected = false;
      }

      if (selected && !existing.length) {
        let attributes;

        const result = await request('cart/add.js', {
          items: [{id, quantity: 1}],
          sections: this.sections(),
          sections_url: location.pathname
        });

        sections = result.sections;

        if (attributes) {
          await request('cart/update.js', {attributes});
        }

        cart = await request('cart.js');
      } else if (!selected && existing.length) {
        for (const item of existing) {
          cart = await request('cart/change.js', {
            id: item.key,
            quantity: 0,
            sections: this.sections(),
            sections_url: location.pathname
          });
        }

        sections = cart.sections;


      }

      const normalized = await this.normalizeSignature(cart);
      await this.publish(normalized, normalized.sections || sections);
    }
  }

  let discountPending = false;

  const enteredCodes = cart => {
    const codes = (cart.discount_codes || []).map(item => item.code);
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap(item =>
        (item.line_level_discount_allocations || []).map(value => value.discount_application)
      )
    ];
    applications.forEach(application => {
      if (application?.type === 'discount_code') codes.push(application.title);
    });
    return [...new Map(codes.filter(Boolean).map(code => [code.toLowerCase(), code])).values()];
  };

  class LCCartDiscounts extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.querySelectorAll('[data-remove-code]').forEach(button => {
        button.disabled = discountPending;
      });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-remove-code]');
        if (!button || !this.contains(button) || discountPending) return;
        event.preventDefault();
        this.removeCode(button.dataset.removeCode);
      }, {signal: this.abort.signal});
    }

    disconnectedCallback() { this.abort?.abort(); }

    status(message, scope = this) {
      const target = scope.querySelector('[data-discount-status]');
      if (target) target.textContent = message;
    }

    async removeCode(code) {
      discountPending = true;
      this.setAttribute('aria-busy', 'true');
      this.status(this.dataset.removing);
      document.querySelectorAll('lc-cart-discounts [data-remove-code]').forEach(button => {
        button.disabled = true;
      });
      const checkoutButtons = [...document.querySelectorAll('.lc-care-surface [name="checkout"]')]
        .map(button => ({button, disabled: button.disabled}));
      checkoutButtons.forEach(({button}) => { button.disabled = true; });
      const page = this.closest('lc-cart-page-offers');
      const surface = page || this.closest('cart-drawer');
      const success = this.dataset.removed;
      const sectionId = element => element?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
      const pageId = sectionId(page);
      let changed = false;

      try {
        await serial(async () => {
          const options = surface?.querySelector('lc-cart-care[data-mode="options"]');
          if (options?.ready) await options.savePreferences();
          const cart = await request('cart.js');
          const remaining = enteredCodes(cart).filter(value => value.toLowerCase() !== code.toLowerCase());
          const sections = [];
          document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true, detail: {sections}
          }));
          if (pageId) sections.unshift(pageId);
          const updated = await request('cart/update.js', {
            discount: remaining.join(','),
            sections: [...new Set(sections)].slice(0, 5),
            sections_url: location.pathname
          });
          changed = true;
          if (enteredCodes(updated).some(value => value.toLowerCase() === code.toLowerCase())) {
            throw new Error('Discount removal not confirmed');
          }
          if (page) {
            const html = updated.sections?.[pageId];
            const replacement = typeof html === 'string'
              ? new DOMParser().parseFromString(html, 'text/html').querySelector('lc-cart-page-offers')
              : null;
            if (!replacement) {
              location.reload();
              return;
            }
            const next = document.importNode(replacement, true);
            page.replaceWith(next);
            this.status(success, next);
            next.querySelector('[data-discount-status]')?.focus({preventScroll: true});
          } else {
            surface?.addEventListener('cart-drawer:refreshed', () => {
              this.status(success, surface);
              surface.querySelector('[data-discount-status]')?.focus({preventScroll: true});
            }, {once: true});
          }
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true, detail: {baseEvent: 'lc-discount:remove', cart: updated}
          }));
        });
      } catch {
        this.status(changed ? this.dataset.refreshError : this.dataset.error);
      } finally {
        discountPending = false;
        this.removeAttribute('aria-busy');
        document.querySelectorAll('lc-cart-discounts [data-remove-code]').forEach(button => {
          button.disabled = false;
        });
        checkoutButtons.forEach(({button, disabled}) => {
          if (button.isConnected) button.disabled = disabled;
        });
      }
    }
  }

  customElements.define('lc-cart-discounts', LCCartDiscounts);

  window.__lcGiftPackaging = {
    requested: () => !!document.querySelector('[data-lc-packaging-line], lc-cart-care [data-lc-service="bag"]:checked'),
    prepare: options => serial(async () => {
      if (!options.config.hasPackaging && !options.config.savedPackagingScope && !window.__lcGiftPackaging.requested()) return;
      const before = await request('cart.js');
      const cart = await options.normalizePackaging(before);
      if (cart !== before && cart.item_count !== before.item_count) {
        await options.publish(cart, cart.sections);
        throw new Error('Packaging charge refreshed. Please review your cart.');
      }
    })
  };

  customElements.define('lc-cart-care', LCCartCare);
})();

(function (host) {
  'use strict';

  const nonempty = value =>
    typeof value === 'string' && value.trim().length > 0;

  function giftIdentity(line) {
    const p = line?.properties;
    if (!p || typeof p !== 'object') return '';

    if (
      nonempty(p._lcn_gwp_tx) &&
      nonempty(p._loshen_gwp_offer) &&
      p._loshen_gwp === p._loshen_gwp_offer
    ) {
      return JSON.stringify([
        'gift-page',
        p._loshen_gwp_offer,
        p._lcn_gwp_tx
      ]);
    }

    const oribe =
      String(line.product_id) === '9159169802488' &&
      String(line.variant_id) === '46663625146616' &&
      p._loshen_gwp === 'Oribe Gift';

    const monthly =
      String(line.product_id) === '9006400635128' &&
      ['50413622427896', '67553983725816', '67554046279928']
        .includes(String(line.variant_id)) &&
      p._loshen_gwp === 'BR Monthly Gift';

    if (
      (oribe || monthly) &&
      typeof p._loshen_gwp_claim === 'string' &&
      /^cart-page-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
        .test(p._loshen_gwp_claim)
    ) {
      return JSON.stringify([
        'cart-page',
        p._loshen_gwp,
        p._loshen_gwp_claim
      ]);
    }

    if (
      oribe &&
      !p._loshen_gwp_claim &&
      !p._lcn_gwp_tx &&
      !p._loshen_gwp_offer
    ) {
      return JSON.stringify(['oribe-gift-card', p._loshen_gwp]);
    }

    return '';
  }

  const owned = line => giftIdentity(line) !== '';

  const validate = cart => {
    if (!cart || !Array.isArray(cart.items)) {
      throw new Error('unverified_cart');
    }

    for (const line of cart.items.filter(owned)) {
      if (
        !Number.isSafeInteger(line.final_line_price) ||
        line.final_line_price < 0 ||
        !Number.isSafeInteger(line.quantity) ||
        line.quantity < 1 ||
        typeof line.key !== 'string' ||
        !line.key.includes(':') ||
        !Number.isSafeInteger(Number(line.variant_id)) ||
        Number(line.variant_id) <= 0
      ) {
        throw new Error('unverified_gift');
      }
    }

    return cart;
  };

  const payable = line =>
    owned(line) &&
    Number.isSafeInteger(line.final_line_price) &&
    line.final_line_price > 0;

  const same = (a, b) =>
    owned(a) &&
    owned(b) &&
    String(a.variant_id) === String(b.variant_id) &&
    giftIdentity(a) === giftIdentity(b);

  class Changed extends Error {}

  async function clean(api, canWrite = () => true) {
    let removed = 0;

    try {
      let cart = validate(await api.cart());

      for (let pass = 0; pass < 24; pass++) {
        const target = cart.items.find(payable);
        if (!target) return {cart, removed};

        if (!canWrite()) {
          throw new Changed('cart_changed');
        }

        const beforeQuantity = cart.items
          .filter(line => same(line, target))
          .reduce((sum, line) => sum + line.quantity, 0);

        cart = validate(await api.remove(target.key));

        const afterQuantity = cart.items
          .filter(line => same(line, target))
          .reduce((sum, line) => sum + line.quantity, 0);

        if (
          cart.items.some(
            line => same(line, target) && line.key === target.key
          ) ||
          afterQuantity !== beforeQuantity - target.quantity
        ) {
          throw new Error('removal_unverified');
        }

        removed++;

        if (!canWrite()) {
          throw new Changed('cart_changed');
        }
      }

      throw new Error('too_many_changes');
    } catch (error) {
      error.removed = removed;
      throw error;
    }
  }

  const core = Object.freeze({
    owned,
    payable,
    same,
    validate,
    clean,
    Changed
  });

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = core;
    return;
  }

  if (
    !host.document ||
    !host.fetch ||
    host.__lcnGiftCartGuard ||
    host.Shopify?.designMode
  ) {
    return;
  }

  const doc = host.document;
  const previousFetch = host.fetch;
  const rawFetch = previousFetch.bind(host);
  const root = () => host.Shopify?.routes?.root || '/';
  const isCart = () => /\/cart\/?$/.test(host.location.pathname);

  const lang = () =>
    (doc.documentElement.lang || 'en')
      .toLowerCase()
      .startsWith('fr') ? 'fr' : 'en';

  const words = {
    en: {
      removed: 'Your complimentary gift was removed because it is no longer free with your current order.',
      error: 'We could not finish checking your complimentary gift. Please retry or remove the gift from your cart before checkout.',
      checking: 'Checking your complimentary gift…',
      retry: 'Check again',
      review: 'Review cart',
      dismiss: 'Dismiss message'
    },
    fr: {
      removed: 'Votre cadeau a été retiré, car il n’est plus offert avec votre commande actuelle.',
      error: 'Nous n’avons pas pu terminer la vérification de votre cadeau. Réessayez ou retirez le cadeau de votre panier avant de passer à la caisse.',
      checking: 'Vérification de votre cadeau…',
      retry: 'Vérifier à nouveau',
      review: 'Voir le panier',
      dismiss: 'Fermer le message'
    }
  };

  const flashKey = 'lcn-gift-safeguard-notice-v1';

  let timer;
  let running;
  let pending = 0;
  let epoch = 0;
  let dirty = true;
  let lastCart = null;
  let message = '';
  let attemptingCheckout = false;
  let replaying = false;
  let refreshing = false;
  let drawing = false;
  let needsPaint = false;

  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

  const themeBusy = () =>
    [...doc.querySelectorAll('lc-cart-care,lc-cart-page-offers')]
      .some(el => el.busy);

  const busy = () =>
    pending > 0 ||
    !!host.__lcnGiftClaimBusy ||
    themeBusy();

  const markedPresent = () => !!lastCart?.items.some(owned);

  const refreshGiftButtons = () => {
    doc.querySelectorAll('loshen-gwp-page').forEach(el => {
      el.refreshButton?.();
    });
  };

  async function json(path, payload) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);

    try {
      const response = await rawFetch(root() + path, {
        method: payload ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          ...(payload ? {'Content-Type': 'application/json'} : {})
        },
        ...(payload ? {body: JSON.stringify(payload)} : {})
      });

      if (!response.ok) {
        throw new Error('cart_request_failed');
      }

      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  const sectionId = element =>
    element?.closest('[id^="shopify-section-"]')
      ?.id.replace('shopify-section-', '');

  function cartSections() {
    return [...new Set([
      sectionId(doc.querySelector('lc-cart-page-offers')),
      sectionId(doc.querySelector('cart-drawer'))
    ].filter(Boolean))].slice(0, 5);
  }

  const api = {
    cart: () => json('cart.js'),

    remove: key => json('cart/change.js', {
      id: key,
      quantity: 0,
      sections: cartSections(),
      sections_url: host.location.pathname + host.location.search
    })
  };

  function storeNotice() {
    try {
      host.sessionStorage.setItem(
        flashKey,
        JSON.stringify({at: Date.now(), message: 'removed'})
      );
    } catch (_) {}
  }

  function mountNotice() {
    if (!message || !doc.body) return;

    if (!doc.getElementById('lcn-gift-safeguard-style')) {
      const style = doc.createElement('style');
      style.id = 'lcn-gift-safeguard-style';

      style.textContent =
        '.lcn-gift-safeguard{display:flex;align-items:center;flex-wrap:wrap;gap:9px 18px;padding:14px 17px;margin:16px 0;border:1px solid #ded9d3;border-radius:7px;background:#f4f1ee;color:#35332f;font:400 13px/1.7 Muli,Mulish,Arial,sans-serif;text-align:left}' +
        '.lcn-gift-safeguard p{flex:1 1 220px;margin:0;font:inherit}' +
        '.lcn-gift-safeguard button,.lcn-gift-safeguard a{font:inherit;font-size:12px;color:inherit;background:none;border:0;padding:4px 0;text-decoration:underline;text-underline-offset:4px;cursor:pointer}' +
        '.lcn-gift-safeguard :focus-visible{outline:2px solid #817568;outline-offset:4px}' +
        'body>.lcn-gift-safeguard{position:fixed;bottom:18px;left:5%;right:5%;z-index:100000;max-width:760px;margin:auto}' +
        'cart-drawer .lcn-gift-safeguard{margin:12px 20px;font-size:12px}';

      doc.head.append(style);
    }

    const targets = [];
    const cartHeader = doc.querySelector('.lc-care-page__header');
    const drawer = doc.querySelector('cart-drawer');

    if (cartHeader) {
      targets.push({
        owner: cartHeader.parentElement,
        anchor: cartHeader
      });
    }

    if (drawer) {
      targets.push({
        owner: drawer,
        anchor: drawer.querySelector('.lc-care__drawer-header')
      });
    }

    if (!targets.length || (!isCart() && message === 'error')) {
      targets.push({owner: doc.body, anchor: null});
    }

    for (const {owner, anchor} of targets) {
      let box = [...owner.children].find(
        el => el.classList.contains('lcn-gift-safeguard')
      );

      if (box?.dataset.message === message && box.lang === lang()) {
        continue;
      }

      if (!box) {
        box = doc.createElement('div');
        box.className = 'lcn-gift-safeguard';
        box.tabIndex = -1;

        if (anchor) anchor.after(box);
        else owner.prepend(box);
      }

      box.dataset.message = message;
      box.lang = lang();
      box.setAttribute('role', message === 'error' ? 'alert' : 'status');
      box.setAttribute(
        'aria-live',
        message === 'error' ? 'assertive' : 'polite'
      );

      const text = doc.createElement('p');
      text.textContent = words[lang()][message];
      box.replaceChildren(text);

      if (message === 'error') {
        const retry = doc.createElement('button');
        retry.type = 'button';
        retry.textContent = words[lang()].retry;

        retry.addEventListener('click', () => {
          setMessage('checking');
          check(true);
        });

        const review = doc.createElement('a');
        review.href = root() + 'cart';
        review.textContent = words[lang()].review;

        box.append(retry, review);
      } else if (message === 'removed') {
        const close = doc.createElement('button');
        close.type = 'button';
        close.textContent = words[lang()].dismiss;
        close.addEventListener('click', () => setMessage(''));

        box.append(close);
      }
    }
  }

  function setMessage(key) {
    message = key;

    if (!key) {
      doc.querySelectorAll('.lcn-gift-safeguard').forEach(el => el.remove());
    } else {
      mountNotice();
    }
  }

  function schedule(delay = 60) {
    dirty = true;
    clearTimeout(timer);

    if (refreshing) return;

    timer = setTimeout(() => {
      if (doc.visibilityState !== 'hidden') check(false);
    }, delay);
  }

  async function idle() {
    const start = Date.now();

    while (busy()) {
      if (Date.now() - start > 8000) {
        throw new Changed('cart_busy');
      }

      await sleep(100);
    }
  }

  async function saveDrafts() {
    const options =
      doc.querySelector('.lc-care-page__summary lc-cart-care[data-mode="options"]') ||
      doc.querySelector('cart-drawer lc-cart-care[data-mode="options"]');

    if (options?.ready && options.savePreferences) {
      await options.savePreferences();
      await host.__lcGiftPackaging.prepare(options);
    }
  }

  function cartFingerprint(cart) {
    return JSON.stringify([
      cart.currency,
      cart.total_price,
      cart.item_count,
      cart.items.map(line => [
        line.key,
        line.quantity,
        line.final_line_price
      ])
    ]);
  }

  function part(cart, id, selector) {
    const html = cart.sections?.[id];
    if (typeof html !== 'string') return null;

    return new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('shopify-section-' + id)
      ?.querySelector(selector) || null;
  }

  function fallbackRefresh() {
    storeNotice();
    refreshing = true;

    const url = isCart()
      ? new URL(host.location.href)
      : new URL(root() + 'cart', host.location.origin);

    url.searchParams.set('lcn_gift_notice', 'removed');
    host.location.replace(url.href);
  }

  function refreshCart(cart) {
    const current = doc.querySelector('lc-cart-page-offers');
    const drawer = doc.querySelector('cart-drawer');
    const pageId = sectionId(current);
    const drawerId = sectionId(drawer);

    const replacement =
      pageId && part(cart, pageId, 'lc-cart-page-offers');

    if (isCart() && !replacement) {
      fallbackRefresh();
      return;
    }

    if (drawer && !part(cart, drawerId, 'cart-drawer')) {
      fallbackRefresh();
      return;
    }

    if (!current && !drawer) {
      fallbackRefresh();
      return;
    }

    if (replacement) {
      try {
        const config = JSON.parse(
          replacement.querySelector('[data-lcp-config]').textContent
        );

        if (cartFingerprint(config.cart) !== cartFingerprint(cart)) {
          fallbackRefresh();
          return;
        }
      } catch (_) {
        fallbackRefresh();
        return;
      }

      const active = current.contains(doc.activeElement)
        ? doc.activeElement
        : null;

      const focusId = active?.id;
      const next = doc.importNode(replacement, true);

      current.replaceWith(next);

      if (focusId) {
        doc.getElementById(focusId)?.focus({preventScroll: true});
      }
    }

    doc.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'lc-cart:change',
        cart,
        lcnGiftGuard: true
      }
    }));

    needsPaint = false;
    setMessage('removed');
    requestAnimationFrame(mountNotice);
  }

  function check(loud = false) {
    if (running) return running;

    running = (async () => {
      let locked = false;

      try {
        await idle();
        await saveDrafts();
        await idle();
        clearTimeout(timer);

        host.__lcnGiftClaimBusy = true;
        locked = true;
        refreshGiftButtons();

        const version = epoch;

        const result = await clean(
          api,
          () => epoch === version && pending === 0 && !themeBusy()
        );

        lastCart = result.cart;

        if (epoch !== version || pending) {
          throw new Changed('cart_changed');
        }

        dirty = false;

        if (result.removed) needsPaint = true;

        if (needsPaint) {
          refreshCart(result.cart);
        } else if (message === 'checking' || message === 'error') {
          setMessage('');
        }

        return {
          ok: true,
          removed: result.removed,
          epoch
        };
      } catch (error) {
        dirty = true;

        if (error.removed) needsPaint = true;
        if (error instanceof Changed) schedule(350);

        if (loud || markedPresent() || isCart() || error.removed) {
          setMessage('error');
        }

        return {
          ok: false,
          removed: error.removed || 0
        };
      } finally {
        if (locked) {
          host.__lcnGiftClaimBusy = false;
          refreshGiftButtons();
        }
      }
    })();

    running.finally(() => {
      running = null;
    });

    return running;
  }

  function isMutation(input, init) {
    try {
      const path = typeof input === 'string' || input instanceof URL
        ? String(input)
        : input.url;

      const url = new URL(path, host.location.href);
      const method = String(
        init?.method || input?.method || 'GET'
      ).toUpperCase();

      return url.origin === host.location.origin &&
        method === 'POST' &&
        /\/cart\/(?:add|change|update|clear)(?:\.js)?\/?$/.test(url.pathname);
    } catch (_) {
      return false;
    }
  }

  host.fetch = function (...args) {
    const watched = isMutation(args[0], args[1]);
    const promise = Reflect.apply(previousFetch, this, args);

    if (watched) {
      pending++;
      epoch++;
      dirty = true;

      const done = () => {
        pending--;
        schedule(60);
      };

      promise.then(done, done);
    }

    return promise;
  };

  function checkoutLink(el) {
    if (!el?.matches('a[href]')) return false;

    try {
      const u = new URL(el.href, host.location.href);

      return u.origin === host.location.origin &&
        /\/checkouts?(?:\/|$)/.test(u.pathname);
    } catch (_) {
      return false;
    }
  }

  function needsCheck() {
    return !!host.__lcGiftPackaging?.requested() ||
      dirty ||
      !lastCart ||
      markedPresent() ||
      pending > 0 ||
      !!running;
  }

  async function approve(replay) {
    if (attemptingCheckout || refreshing) return;

    attemptingCheckout = true;

    try {
      if (markedPresent()) setMessage('checking');

      const result = await check(true);

      if (!result.ok || result.removed || refreshing) return;

      if (dirty || pending || epoch !== result.epoch || busy()) {
        setMessage('error');
        return;
      }

      replaying = true;

      try {
        replay();
      } finally {
        replaying = false;
      }
    } finally {
      attemptingCheckout = false;
    }
  }

  doc.addEventListener('click', event => {
    if (replaying || event.defaultPrevented || event.button > 0) return;

    const el = event.target instanceof Element
      ? event.target.closest('button,input,a')
      : null;

    if (
      !el ||
      !(el.name === 'checkout' || checkoutLink(el)) ||
      !needsCheck()
    ) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    approve(() => {
      if (el.isConnected) el.click();
    });
  }, true);

  doc.addEventListener('submit', event => {
    if (replaying || event.defaultPrevented || !needsCheck()) return;

    const form = event.target;
    const submitter = event.submitter;

    if (!(form instanceof HTMLFormElement)) return;

    const action = new URL(form.action, host.location.href);

    const checkout =
      submitter?.name === 'checkout' ||
      !!form.querySelector('input[type="hidden"][name="checkout"]') ||
      (
        action.origin === host.location.origin &&
        /\/checkouts?(?:\/|$)/.test(action.pathname)
      );

    if (!checkout) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    approve(() => {
      if (form.isConnected) {
        form.requestSubmit(
          submitter?.isConnected ? submitter : undefined
        );
      }
    });
  }, true);

  ['cart:change', 'cart:refresh', 'line-item:change'].forEach(name => {
    doc.addEventListener(name, event => {
      if (!event.detail?.lcnGiftGuard) {
        epoch++;
        schedule();
      }
    });
  });

  host.addEventListener('focus', () => schedule());

  host.addEventListener('pageshow', event => {
    if (event.persisted) schedule();
  });

  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'visible') schedule();
  });

  const observer = new MutationObserver(() => {
    if (!message || drawing) return;

    drawing = true;

    requestAnimationFrame(() => {
      drawing = false;
      mountNotice();
    });
  });

  observer.observe(doc.documentElement, {
    childList: true,
    subtree: true
  });

  function start() {
    try {
      const stored = JSON.parse(
        host.sessionStorage.getItem(flashKey) || 'null'
      );

      host.sessionStorage.removeItem(flashKey);

      if (
        stored?.message === 'removed' &&
        Date.now() - stored.at < 300000
      ) {
        setMessage('removed');
      }
    } catch (_) {}

    const url = new URL(host.location.href);

    if (url.searchParams.get('lcn_gift_notice') === 'removed') {
      setMessage('removed');
      url.searchParams.delete('lcn_gift_notice');
      host.history.replaceState(host.history.state, '', url.href);
    }

    schedule(50);
  }

  host.__lcnGiftCartGuard = Object.freeze({
    version: '1.2.0',
    check: () => check(true)
  });

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', start, {once: true});
  } else {
    start();
  }
})(typeof window === 'undefined' ? globalThis : window);

